/* AthleteProfileHero — iter454.2
 * ─────────────────────────────────────────────────────────────────────
 * Jay's "Jaxon" layout: horizontal split card at the top of the network
 * profile.
 *   ┌────────────────────────────────────────┐
 *   │ DREAMS · NETWORK · SPORT               │
 *   │ NAME  [PRO]     ┌──────── photo ──────┐│
 *   │ POSITION · ST   │  fades to black on  ││
 *   │ School          │  its LEFT edge so   ││
 *   │ City, ST        │  the identity block ││
 *   │ [pills]         │  reads cleanly over ││
 *   │ [actions]       │  pure black         ││
 *   │ [lock screen]   │                     ││
 *   │ [socials]       │                     ││
 *   └────────────────────────────────────────┘
 *
 * Photo sits on the RIGHT ~55% of the card, absolutely positioned so
 * it always covers the right edge. A horizontal gradient (black → clear
 * → clear) fades the photo's LEFT edge into the background so text on
 * the left never fights the image. Works identically for any athlete
 * because the fade math is a fixed percentage.
 *
 * All owner-only edit affordances (preview toggle, "Tap to change"
 * pill, verified/school badges) are hoisted via props so the layout
 * stays neutral for viewers.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Move, Check, X, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from '../ImageUpload';
import AthleteAvatarFallback from './AthleteAvatarFallback';
import VerifiedCheck from '../VerifiedCheck';
import HopeBadge from '../HopeBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AthleteProfileHero = ({
  athlete,
  isOwner,
  isVisionary = false,
  backHref,
  topRight = null,
  actions = null,
  onEditPhoto,
  onSaved,
  // `compact` renders a small, non-interactive, read-only version of
  // this exact same hero — used to give the athlete a live "this is
  // what your Network profile actually looks like" preview inside the
  // PushToNetworkPill drawer. Same component, same data, so it can
  // never drift out of sync with the real page. Fixed (non-viewport-
  // relative) sizing, since it's embedded in a fixed-width card rather
  // than filling the screen.
  compact = false,
}) => {
  // iter454.3 · Drag-to-reposition state. Focal is stored as
  // {x,y} percentages that drive CSS object-position. Owners can
  // enter reposition mode via the "Reposition Photo" button anchored
  // to the middle of the photo (only when they're NOT in preview
  // mode — preview shows the visitor experience).
  const initialFocal = {
    x: athlete?.network_hero_focal?.x ?? 50,
    y: athlete?.network_hero_focal?.y ?? 50,
  };
  const [repositionMode, setRepositionMode] = useState(false);
  const [focal, setFocal] = useState(initialFocal);
  const [savedFocal, setSavedFocal] = useState(initialFocal);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef({ active: false, sx: 0, sy: 0, fx: 50, fy: 50, w: 0, h: 0 });

  // Keep local focal in sync when the athlete doc changes (e.g. parent
  // patches after an unrelated field save).
  useEffect(() => {
    const next = {
      x: athlete?.network_hero_focal?.x ?? 50,
      y: athlete?.network_hero_focal?.y ?? 50,
    };
    setFocal(next);
    setSavedFocal(next);
  }, [athlete?.slug, athlete?.network_hero_focal?.x, athlete?.network_hero_focal?.y]);

  if (!athlete) return null;

  const hero =
    athlete.network_hero_image ||
    athlete.photo ||
    athlete.network_card_image ||
    '';

  const pills = [];
  if (athlete.position) pills.push({ key: 'pos', text: athlete.position });
  if (athlete.jersey_number) pills.push({ key: 'jersey', text: `#${athlete.jersey_number}` });
  if (athlete.grade_year || athlete.graduation_year) {
    pills.push({
      key: 'class',
      text: `Class of ${athlete.grade_year || athlete.graduation_year}`,
    });
  }
  // iter459d · Competition level is now surfaced ONLY as the amber
  // badge chip immediately next to the athlete's bold name (see
  // `athlete.competition_level && <chip>` render below). Displaying
  // it again in this pill row felt redundant on the network profile
  // (Jaxon showed "PRO" twice — once next to the name, once in the
  // metadata pill row). Keeping the source of truth next to the name
  // makes the hierarchy read cleaner on mobile.

  const school = athlete.school_name || athlete.school || '';
  const location = athlete.hometown || athlete.school_state || '';
  const positionLine = [
    athlete.position,
    athlete.school_state || (athlete.hometown && athlete.hometown.split(',').slice(-1)[0].trim()),
  ]
    .filter(Boolean)
    .join(' · ')
    .toUpperCase();

  // ─── Drag handlers ────────────────────────────────────────────────
  // Pointer events cover mouse + touch uniformly. Deltas are converted
  // to percentages relative to the image container so drag speed stays
  // proportional across viewport sizes. Focal is clamped 0-100.
  const handlePointerDown = (e) => {
    if (!repositionMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      active: true,
      sx: e.clientX,
      sy: e.clientY,
      fx: focal.x,
      fy: focal.y,
      w: rect.width,
      h: rect.height,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!repositionMode || !d.active) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    // Drag direction ↔ object-position direction:
    // dragging RIGHT (dx>0) reveals more of the LEFT of the image →
    // decrease X. Same logic for vertical. We halve the delta so a
    // full-width swipe covers ~50% of positional range (feels smooth,
    // never wraps past the edges too fast).
    const newX = Math.max(0, Math.min(100, d.fx - (dx / d.w) * 50));
    const newY = Math.max(0, Math.min(100, d.fy - (dy / d.h) * 50));
    setFocal({ x: newX, y: newY });
  };

  const handlePointerUp = (e) => {
    if (dragRef.current.active) {
      dragRef.current.active = false;
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    }
  };

  const enterReposition = () => {
    setRepositionMode(true);
    setFocal(savedFocal);
  };
  const cancelReposition = () => {
    setRepositionMode(false);
    setFocal(savedFocal);
  };
  const saveReposition = async () => {
    // No-op if nothing changed — avoids a wasted PUT + toast spam.
    if (
      Math.round(focal.x) === Math.round(savedFocal.x) &&
      Math.round(focal.y) === Math.round(savedFocal.y)
    ) {
      setRepositionMode(false);
      return;
    }
    setSaving(true);
    try {
      const payload = { network_hero_focal: {
        x: Math.round(focal.x * 10) / 10,
        y: Math.round(focal.y * 10) / 10,
      } };
      await axios.put(`${API}/me/athlete/profile`, { updates: payload }, { withCredentials: true });
      setSavedFocal(focal);
      setRepositionMode(false);
      toast.success('Photo position saved — live on the network');
      onSaved?.(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="relative w-full bg-black overflow-hidden"
      data-testid="athlete-profile-hero"
    >
      {/* iter454.15 + iter483 · Inner centering wrapper. Widens
          responsively so iPad renders as a real tablet layout
          instead of a stretched mobile column: mobile stays at the
          natural width, iPad portrait picks up 1024px (md:max-w-5xl),
          iPad landscape / Pro land on 1152px (lg:max-w-6xl). The hero
          photo now gets more room on the right without the identity
          block stretching to unreadable line lengths. */}
      <div className="relative w-full max-w-3xl md:max-w-5xl lg:max-w-6xl mx-auto">
      {/* Photo layer — absolutely positioned to the right. During
          reposition mode we raise it above the content grid (z-40)
          so drag gestures never get intercepted by the identity
          block that overlaps ~24% of the photo's left edge. In
          normal mode it stays under the grid (z-5) so the identity
          buttons remain the primary tap target. */}
      <div
        className={`absolute inset-y-0 right-0 left-[38%] sm:left-[35%] ${
          repositionMode ? 'z-40 cursor-grabbing touch-none select-none' : 'z-[5]'
        } ${!repositionMode && isOwner ? 'cursor-pointer' : ''}`}
        onClick={isOwner && !repositionMode ? onEditPhoto : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        role={isOwner ? 'button' : undefined}
        aria-label={
          repositionMode
            ? 'Drag the photo to reposition, then hit save'
            : isOwner ? 'Tap to change hero photo' : undefined
        }
        data-testid="athlete-hero-photo-target"
      >
        {hero ? (
          <img
            src={resolveImageUrl(hero)}
            alt={athlete.name}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className={`absolute inset-0 w-full h-full object-cover ${
              repositionMode ? 'transition-none' : 'transition-[object-position] duration-200'
            }`}
            style={{ objectPosition: `${focal.x}% ${focal.y}%` }}
            draggable={false}
            data-testid="athlete-hero-photo"
          />
        ) : (
          <AthleteAvatarFallback
            athlete={athlete}
            className="absolute inset-0 w-full h-full"
            ringWidth={0}
            showInitials
            forceInitials
          />
        )}
        {/* iter454.4 · Lighter horizontal gradient — photo is much more
            visible now. During reposition mode we dial it back FURTHER
            so the athlete can see their face while dragging. */}
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-200"
          style={{
            background:
              'linear-gradient(90deg, #000 0%, rgba(0,0,0,0.72) 14%, rgba(0,0,0,0.15) 46%, transparent 78%)',
            opacity: repositionMode ? 0.35 : 1,
          }}
          aria-hidden
        />
        <div
          className="absolute inset-x-0 bottom-0 h-14 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, transparent 0%, #000 100%)' }}
          aria-hidden
        />

        {/* iter454.7 · Reposition mode overlay — hint + Save/Cancel now
            live in the SAME pill at the top of the photo so the athlete
            can drag → save without moving their finger to the bottom. */}
        {repositionMode && (
          <div
            className="absolute inset-x-3 top-3 z-20 flex items-center justify-between gap-2 rounded-full bg-black/80 ring-1 ring-white/20 backdrop-blur-md pl-3 pr-1.5 py-1.5"
            data-testid="reposition-hint"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-white/85 pointer-events-none">
              <Move className="w-3 h-3" />
              Drag to reposition
            </span>
            <span className="inline-flex items-center gap-1.5">
              <button
                type="button"
                onClick={cancelReposition}
                disabled={saving}
                className="inline-flex items-center px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white/85 text-[10px] font-black transition"
                aria-label="Cancel reposition"
                data-testid="reposition-cancel-btn"
              >
                <X className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={saveReposition}
                disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-[10px] uppercase tracking-widest font-black transition disabled:opacity-60"
                aria-label="Save photo position"
                data-testid="reposition-save-btn"
              >
                <Check className="w-3 h-3" />
                {saving ? '…' : 'Save'}
              </button>
            </span>
          </div>
        )}

        {/* iter454.4 · Reposition button — moved INSIDE the photo target
            so it's centered on the actual photo (not the right grid
            cell, which is only 38% wide and put the button off to the
            right of the photo's true center). Uses stopPropagation so
            tapping it doesn't fall through to the photo's onClick
            (which opens the Basics editor / change-photo flow). */}
        {isOwner && !repositionMode && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); enterReposition(); }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 ring-1 ring-white/25 backdrop-blur-md text-white text-[10px] uppercase tracking-widest font-black hover:bg-black/85 transition shadow-2xl"
            aria-label="Reposition photo"
            data-testid="reposition-photo-btn"
          >
            <Move className="w-3.5 h-3.5" />
            Reposition
          </button>
        )}

        {/* iter454.16 · Removed the redundant "Tap" camera chip that
            sat on the photo layer — the labeled "Edit" pill in the
            right column (below) is now the single, unambiguous
            change-photo affordance so athletes know exactly what to
            tap. */}
      </div>

      {/* Content grid — sits ABOVE the photo layer visually, but is
          pointer-events-none so clicks over transparent grid space
          pass through to the photo target below. LEFT column re-enables
          pointer-events for its identity + action content; RIGHT column
          only re-enables events on interactive children (topRight,
          save/cancel). This is what allows the reposition button
          inside the photo layer to actually receive taps. */}
      <div
        className="relative z-10 grid grid-cols-[minmax(0,1fr)_38%] sm:grid-cols-[minmax(0,1fr)_35%] pointer-events-none"
        style={{ minHeight: compact ? '150px' : 'clamp(340px, 62vw, 520px)' }}
      >
        {/* Left column — identity block.
            iter516g · Added `min-w-0 max-w-full overflow-hidden` so
            child buttons/pills can never bleed past the column's
            shrunk width — was causing the "Add to Lock Screen" pill
            and Report menu to overflow off the LEFT of the viewport
            on narrow mobile widths (bug caught on TestFlight). */}
        <div className={`flex flex-col px-4 sm:px-6 pt-4 sm:pt-5 pb-5 gap-3 pointer-events-auto min-w-0 max-w-full overflow-hidden ${compact ? '!px-3 !pt-3 !pb-3 !gap-1.5' : ''}`}>
          {!compact && (
            <div className="flex items-start justify-between gap-2">
              <Link
                to={backHref || (isVisionary ? '/visionary/network' : '/')}
                className="inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full bg-white/[0.08] ring-1 ring-white/15 hover:bg-white/[0.14] text-white/85 text-[11px] transition"
                data-testid="back-to-network"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </Link>
            </div>
          )}

          {/* iter459g · Eyebrow above the athlete's name. Prior copy
              ("Dreams League · Network · Football") was busy and
              redundant with the metadata pills below. User asked
              for a single, small "FLYT ID™" tag — same across every
              athlete regardless of channel/sport. Sizing dropped
              to 8pt tracking-widest so it reads as a brand watermark
              rather than a headline. */}
          <p
            className="text-[8px] uppercase tracking-[0.32em] text-white/40 font-bold leading-tight"
            data-testid="athlete-hero-eyebrow"
          >
            FLYT ID™
          </p>

          <div className="flex items-start gap-2 flex-wrap -mt-1">
            <h1
              className="font-display uppercase text-white leading-[0.88] tracking-tight break-words"
              style={{ fontSize: compact ? '1.5rem' : 'clamp(2rem, 10.5vw, 4.25rem)' }}
              data-testid="athlete-name"
            >
              {(athlete.name || '').split(' ').slice(0, 3).map((word, i) => (
                <span key={i} className="block">
                  {word}
                </span>
              ))}
            </h1>
            <div className="flex flex-col gap-1.5 mt-1">
              {athlete.competition_level && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-black bg-yellow-400/15 text-yellow-200 ring-1 ring-yellow-400/40"
                  data-testid="athlete-competition-level"
                >
                  {athlete.competition_level}
                </span>
              )}
              {athlete.verified && (
                <span
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 shadow-lg"
                  aria-label="Verified"
                >
                  <VerifiedCheck size={11} />
                </span>
              )}
              {athlete.hope && <HopeBadge size="sm" />}
            </div>
          </div>

          {positionLine && (
            <p className="text-[11px] sm:text-xs font-black uppercase tracking-[0.18em] text-blue-300 -mt-1">
              {positionLine}
            </p>
          )}

          {(school || location) && (
            <p
              className="text-[11px] sm:text-xs text-white/80 leading-snug"
              data-testid="athlete-school-line"
            >
              {school && <span className="block">{school}</span>}
              {location && <span className="block text-white/60">{location}</span>}
            </p>
          )}

          {/* iter454.5 · Tag pills. Values render as static pills for
              viewers + owners; missing fields become "+ Add …" chips
              for OWNERS only so they can quickly fill in Position /
              Jersey / Class / Sport Level without leaving the profile.
              Tapping any "+ Add" chip opens the shared Basics editor. */}
          {(pills.length > 0 || isOwner) && (
            <div
              className="flex flex-wrap items-center gap-1"
              data-testid="athlete-hero-pills"
            >
              {pills.map((p) => (
                <span
                  key={p.key}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold ring-1 ${
                    p.accent
                      ? 'bg-yellow-400/15 ring-yellow-300/40 text-yellow-200'
                      : 'bg-white/[0.08] ring-white/15 text-white/85'
                  }`}
                  data-testid={`hero-pill-${p.key}`}
                >
                  {p.text}
                </span>
              ))}
              {isOwner && !athlete.position && (
                <button
                  type="button"
                  onClick={onEditPhoto}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold ring-1 ring-dashed ring-white/30 text-white/60 hover:text-white hover:ring-white/60 transition"
                  data-testid="hero-add-position"
                  aria-label="Add position"
                >
                  <Plus className="w-2.5 h-2.5" /> Position
                </button>
              )}
              {isOwner && !athlete.jersey_number && (
                <button
                  type="button"
                  onClick={onEditPhoto}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold ring-1 ring-dashed ring-white/30 text-white/60 hover:text-white hover:ring-white/60 transition"
                  data-testid="hero-add-jersey"
                  aria-label="Add jersey number"
                >
                  <Plus className="w-2.5 h-2.5" /> #
                </button>
              )}
              {isOwner && !athlete.grade_year && !athlete.graduation_year && (
                <button
                  type="button"
                  onClick={onEditPhoto}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold ring-1 ring-dashed ring-white/30 text-white/60 hover:text-white hover:ring-white/60 transition"
                  data-testid="hero-add-class"
                  aria-label="Add class year"
                >
                  <Plus className="w-2.5 h-2.5" /> Class
                </button>
              )}
              {isOwner && !athlete.competition_level && (
                <button
                  type="button"
                  onClick={onEditPhoto}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold ring-1 ring-dashed ring-yellow-300/50 text-yellow-200/80 hover:text-yellow-100 hover:ring-yellow-200 transition"
                  data-testid="hero-add-level"
                  aria-label="Add sport level"
                >
                  <Plus className="w-2.5 h-2.5" /> Level
                </button>
              )}
            </div>
          )}

          {actions && (
            // iter516g · Added `min-w-0 max-w-full` on the actions
            // wrapper so pills stay inside the identity column and
            // don't overflow off-screen on narrow viewports.
            <div className="mt-1 flex flex-col gap-2 min-w-0 max-w-full" data-testid="athlete-hero-actions">
              {actions}
            </div>
          )}
        </div>

        {/* Right column — reserved for the photo. Anchors the top-right
            slot and the centered "Reposition Photo" button. Uses
            pointer-events-none on the column itself so drag gestures
            pass through empty space to the photo below; interactive
            children re-enable pointer-events. */}
        <div className="relative pointer-events-none">
          {topRight && !repositionMode && (
            <div
              className="absolute top-3 right-3 z-20 inline-flex items-center gap-1.5 flex-wrap justify-end max-w-[90%] pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="athlete-hero-topright"
            >
              {topRight}
            </div>
          )}
          {/* iter454.3 · Reposition button moved INTO the photo target
              (see above) — this column now only hosts the top-right
              slot and save/cancel controls. */}
          {/* iter454.7 · Save / Cancel now live at the TOP of the photo
              next to "Drag to reposition" (see photo layer above) so the
              athlete can save with their finger already inside the drag
              surface. Nothing to render in the right column during
              reposition mode. */}
          {/* iter454.16 · Bottom-right "Edit" pill. Was previously an
              icon-only chevron so athletes had no idea it changed the
              photo. Now it's a labeled Camera + "Edit" pill matching
              the rest of the Flythouse UI — one clear tap target for
              "change my hero photo" on the network profile. */}
          {isOwner && !repositionMode && (
            <button
              type="button"
              onClick={onEditPhoto}
              className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 ring-1 ring-white/25 backdrop-blur-md hover:bg-black/85 transition pointer-events-auto shadow-lg"
              aria-label="Edit hero photo"
              data-testid="athlete-hero-edit-btn"
            >
              <Camera className="w-3.5 h-3.5 text-white" />
              <span className="text-[10px] uppercase tracking-widest font-black text-white">Edit</span>
            </button>
          )}
        </div>
      </div>
      </div>{/* /iter454.15 max-w-3xl inner centering */}
    </div>
  );
};

export default AthleteProfileHero;

